/**
 * wakeWindowUtils — pure helpers for the Wake Window Recommendations card.
 *
 * Wake window data sourced from published sleep science (Huckleberry /
 * Precious Little Sleep / AAP). All functions are pure and side-effect-free
 * so they are easily unit-testable.
 */

/** Minimum and maximum awake time (in minutes) by age band (weeks). */
const WAKE_WINDOWS = [
  { minWeeks: 0,   maxWeeks: 6,   minMin: 45,  maxMin: 60  }, // Newborn
  { minWeeks: 6,   maxWeeks: 12,  minMin: 60,  maxMin: 90  }, // 6–12 weeks
  { minWeeks: 12,  maxWeeks: 20,  minMin: 75,  maxMin: 120 }, // 3–5 months
  { minWeeks: 20,  maxWeeks: 32,  minMin: 120, maxMin: 150 }, // 5–8 months
  { minWeeks: 32,  maxWeeks: 44,  minMin: 150, maxMin: 210 }, // 8–11 months
  { minWeeks: 44,  maxWeeks: 60,  minMin: 180, maxMin: 240 }, // 11–15 months
  { minWeeks: 60,  maxWeeks: 78,  minMin: 240, maxMin: 300 }, // 15–18 months
  { minWeeks: 78,  maxWeeks: 104, minMin: 270, maxMin: 360 }, // 18m–2 years
  // 104+ weeks (≥2 years): no nap guidance — show informational banner
];

/** Age at which wake window nap guidance is no longer shown by default. */
export const WAKE_WINDOW_CUTOFF_WEEKS = 104; // 2 years

// ── Core helpers ────────────────────────────────────────────────────────────

/**
 * Returns the child's age in whole weeks from their date of birth string,
 * or null if the DOB is missing or unparseable.
 */
export function getAgeWeeks(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;
  return Math.floor((Date.now() - dob.getTime()) / (7 * 24 * 60 * 60 * 1000));
}

/**
 * Returns the wake window band for a given age in weeks, or null if the
 * child is older than the guidance cutoff (≥2 years).
 *
 * @param {number|null} ageWeeks
 * @returns {{ minMin: number, maxMin: number } | null}
 */
export function getWakeWindow(ageWeeks) {
  if (ageWeeks == null) return null;
  return WAKE_WINDOWS.find((w) => ageWeeks >= w.minWeeks && ageWeeks < w.maxWeeks) ?? null;
}

/**
 * Returns the LOCAL date string (YYYY-MM-DD) for a raw date value.
 *
 * Must use local date methods (getFullYear/getMonth/getDate), not
 * ISO split('T')[0], because the timer logger stores `date` as midnight
 * LOCAL time converted to ISO — which in timezones ahead of UTC (e.g. BST)
 * is 23:xx the previous UTC day, making split('T')[0] return the wrong date.
 *
 * @param {string|Date|object} raw
 * @returns {string|null}  "YYYY-MM-DD" in local time, or null
 */
function localDateStrFromRaw(raw) {
  if (!raw) return null;
  const d = raw?.toDate instanceof Function ? raw.toDate() : new Date(raw);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Reconstructs the end Date for a sleep entry, correctly advancing to the
 * next day when endTime < startTime (i.e. the session crossed midnight).
 *
 * @param {object} item  - sleep entry with { date|createdAt, startTime, endTime }
 * @returns {Date|null}
 */
function resolveSleepEndDate(item) {
  const raw = item.date ?? item.createdAt;
  const dateStr = localDateStrFromRaw(raw);
  if (!dateStr) return null;
  let endDt = new Date(`${dateStr}T${item.endTime}`);
  if (isNaN(endDt.getTime())) return null;
  // If end time is earlier than start time the session crossed midnight —
  // advance the end date by one day.
  if (item.startTime && item.endTime < item.startTime) {
    endDt = new Date(endDt.getTime() + 24 * 60 * 60 * 1000);
  }
  return endDt;
}

/**
 * Finds the most recent wake-up time across ALL completed sleep entries
 * (naps and night sleep). Used for the Wake Window card so that night sleep
 * ending at e.g. 04:27 correctly resets the awake counter.
 *
 * Handles overnight sessions: if endTime < startTime the end date is advanced
 * by one day before comparison.
 *
 * @param {Array} sleepItems  - raw Firestore sleep tracking entries
 * @returns {Date|null}
 */
export function getLastSleepEndTime(sleepItems) {
  if (!sleepItems?.length) return null;

  const ends = sleepItems
    .filter((item) => item.endTime && item.startTime)
    .map(resolveSleepEndDate)
    .filter(Boolean)
    .sort((a, b) => b - a); // newest first

  return ends[0] ?? null;
}

/**
 * Finds the most recent completed daytime nap from an array of sleep items.
 *
 * "Daytime" = startTime hour is 06:00–19:59 (before 8 pm).
 * "Completed" = the item has an endTime field.
 *
 * Returns a Date representing when the nap ended, or null if none found.
 * Used by NapReminderBanner (nap-specific logic). For wake windows use
 * getLastSleepEndTime instead.
 *
 * @param {Array} sleepItems  - raw Firestore sleep tracking entries
 * @returns {Date|null}
 */
export function getLastNapEndTime(sleepItems) {
  if (!sleepItems?.length) return null;

  const napEnds = sleepItems
    .filter((item) => {
      if (!item.endTime) return false;
      const startHour = item.startTime
        ? parseInt(item.startTime.split(':')[0], 10)
        : null;
      // Daytime: 06:00 ≤ startHour < 20:00
      return startHour != null && startHour >= 6 && startHour < 20;
    })
    .map(resolveSleepEndDate)
    .filter(Boolean)
    .sort((a, b) => b - a); // newest first

  return napEnds[0] ?? null;
}

/**
 * Computes the current wake status given a wake window and the time the
 * last nap ended.
 *
 * @param {{ minMin: number, maxMin: number }} wakeWindow
 * @param {Date} lastNapEnd
 * @param {number} [now]  - timestamp override for testing (defaults to Date.now())
 * @returns {{
 *   status: 'too-early'|'approaching'|'open'|'overtired',
 *   awakeMin: number,
 *   minutesUntilWindow?: number,
 *   minutesOverdue?: number,
 * } | null}
 */
export function getWakeStatus(wakeWindow, lastNapEnd, now = Date.now()) {
  if (!wakeWindow || !lastNapEnd) return null;

  const awakeMin = Math.floor((now - lastNapEnd.getTime()) / 60000);
  if (awakeMin < 0) return null; // clock skew / future date

  const { minMin, maxMin } = wakeWindow;

  if (awakeMin < Math.floor(minMin * 0.75)) {
    return { status: 'too-early',   awakeMin, minutesUntilWindow: minMin - awakeMin };
  }
  if (awakeMin < minMin) {
    return { status: 'approaching', awakeMin, minutesUntilWindow: minMin - awakeMin };
  }
  if (awakeMin <= maxMin) {
    return { status: 'open',        awakeMin };
  }
  return   { status: 'overtired',   awakeMin, minutesOverdue: awakeMin - maxMin };
}

/**
 * Analyses recent night sleep entries to determine the baby's typical bedtime.
 *
 * "Bedtime" entries are those where startTime hour falls between 18:00 and 23:59
 * (6 pm to midnight). Earlier starts (post-noon) are daytime naps; starts after
 * midnight are night wakings, not bedtimes.
 *
 * The function sorts all night sleep entries by their full datetime (newest first),
 * takes the most recent `maxNights`, then averages their start times.
 *
 * Returns null when fewer than `minSamples` qualifying entries exist — not enough
 * data to make a meaningful prediction.
 *
 * @param {Array}  sleepItems  - raw Firestore sleep tracking entries
 * @param {number} minSamples  - minimum entries required before showing guidance (default 3)
 * @param {number} maxNights   - how many recent nights to include in the average (default 14)
 * @returns {{ averageMinutes: number, sampleCount: number } | null}
 */
export function getTypicalBedtime(sleepItems, minSamples = 3, maxNights = 14) {
  if (!sleepItems?.length) return null;

  // Build a list of { startMinutes, fullDt } for each night sleep entry
  const candidates = sleepItems
    .filter((item) => {
      if (!item.startTime) return false;
      const hour = parseInt(item.startTime.split(':')[0], 10);
      return hour >= 18 && hour <= 23;
    })
    .map((item) => {
      const [h, m] = item.startTime.split(':').map(Number);
      const startMinutes = h * 60 + m;

      // Reconstruct full datetime so we can sort by recency
      const raw = item.date ?? item.createdAt;
      const dateStr = localDateStrFromRaw(raw);
      if (!dateStr) return null;
      const fullDt = new Date(`${dateStr}T${item.startTime}`);
      return isNaN(fullDt.getTime()) ? null : { startMinutes, fullDt };
    })
    .filter(Boolean)
    .sort((a, b) => b.fullDt - a.fullDt) // newest first
    .slice(0, maxNights);

  if (candidates.length < minSamples) return null;

  const averageMinutes = Math.round(
    candidates.reduce((sum, c) => sum + c.startMinutes, 0) / candidates.length
  );

  return { averageMinutes, sampleCount: candidates.length };
}

// ── Daily sleep targets ──────────────────────────────────────────────────────

/**
 * Age-appropriate total daily sleep targets (naps + night sleep combined).
 * Midpoints of published AAP / Huckleberry ranges. Used for the sleep debt indicator.
 */
const DAILY_SLEEP_TARGETS = [
  { minWeeks: 0,   maxWeeks: 6,   targetMin: 990 }, // ~16.5 h
  { minWeeks: 6,   maxWeeks: 12,  targetMin: 900 }, // 15 h
  { minWeeks: 12,  maxWeeks: 20,  targetMin: 840 }, // 14 h
  { minWeeks: 20,  maxWeeks: 78,  targetMin: 780 }, // 13 h
  { minWeeks: 78,  maxWeeks: 104, targetMin: 750 }, // 12.5 h
  { minWeeks: 104, maxWeeks: 999, targetMin: 690 }, // 11.5 h
];

/**
 * Returns the age-appropriate daily sleep target in minutes, or null if the
 * child's age cannot be determined.
 *
 * @param {number|null} ageWeeks
 * @returns {number|null}
 */
export function getDailySleepTarget(ageWeeks) {
  if (ageWeeks == null) return null;
  return DAILY_SLEEP_TARGETS.find(
    (b) => ageWeeks >= b.minWeeks && ageWeeks < b.maxWeeks
  )?.targetMin ?? null;
}

/**
 * Calculates today's total logged sleep in minutes from a sleep entries array.
 *
 * Handles overnight sessions: a sleep that started yesterday and ended this
 * morning counts the portion that falls within today's calendar day (midnight
 * to now). Uses local date methods throughout to avoid UTC-vs-local mismatches.
 *
 * @param {Array} sleepItems  - raw Firestore sleep tracking entries
 * @returns {number}  total minutes (0 if nothing logged today)
 */
export function getTodaySleepMinutes(sleepItems) {
  if (!sleepItems?.length) return 0;

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  return sleepItems.reduce((total, item) => {
    if (!item.startTime || !item.endTime) return total;
    const raw = item.date ?? item.createdAt;
    const dateStr = localDateStrFromRaw(raw);
    if (!dateStr) return total;

    const sessionStart = new Date(`${dateStr}T${item.startTime}`);
    let   sessionEnd   = new Date(`${dateStr}T${item.endTime}`);
    if (isNaN(sessionStart.getTime()) || isNaN(sessionEnd.getTime())) return total;
    // Advance end by one day for overnight sessions
    if (item.endTime < item.startTime) {
      sessionEnd = new Date(sessionEnd.getTime() + 24 * 60 * 60 * 1000);
    }

    // Skip entries that don't overlap with today at all
    if (sessionEnd <= todayStart || sessionStart > todayEnd) return total;

    // Clamp to today's window so overnight sleeps only contribute the portion
    // that falls within today (e.g. 23:00–07:00 → count 00:00–07:00 today)
    const effectiveStart = sessionStart < todayStart ? todayStart : sessionStart;
    const effectiveEnd   = sessionEnd   > todayEnd   ? todayEnd   : sessionEnd;

    const mins = Math.floor((effectiveEnd - effectiveStart) / 60000);
    return total + (mins > 0 ? mins : 0);
  }, 0);
}

/**
 * Counts completed daytime naps logged today and returns the most recent
 * nap end time, for the nap count strip on the dashboard.
 *
 * @param {Array} sleepItems  - raw Firestore sleep tracking entries
 * @returns {{ count: number, lastNapEnd: Date|null }}
 */
export function getTodayNapStats(sleepItems) {
  if (!sleepItems?.length) return { count: 0, lastNapEnd: null };

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  const todayNaps = sleepItems.filter((item) => {
    if (!item.startTime || !item.endTime) return false;
    const raw = item.date ?? item.createdAt;
    const dateStr = localDateStrFromRaw(raw);
    if (!dateStr) return false;

    const hour = parseInt(item.startTime.split(':')[0], 10);
    if (hour < 5 || hour >= 20) return false; // daytime naps only

    // Include nap if it started today (use local date comparison)
    const sessionStart = new Date(`${dateStr}T${item.startTime}`);
    return sessionStart >= todayStart && sessionStart <= todayEnd;
  });
  const ends = todayNaps.map(resolveSleepEndDate).filter(Boolean).sort((a, b) => b - a);
  return { count: todayNaps.length, lastNapEnd: ends[0] ?? null };
}

/**
 * Computes the standard deviation of bedtime start times (in minutes since
 * midnight) across recent nights, as a measure of bedtime consistency.
 *
 * Returns null when fewer than minSamples qualifying nights exist.
 *
 * @param {Array}  sleepItems
 * @param {number} minSamples  (default 3)
 * @param {number} maxNights   (default 14)
 * @returns {{ stdDevMinutes: number, sampleCount: number } | null}
 */
export function getBedtimeConsistency(sleepItems, minSamples = 3, maxNights = 14) {
  if (!sleepItems?.length) return null;
  const candidates = sleepItems
    .filter((item) => {
      if (!item.startTime) return false;
      const hour = parseInt(item.startTime.split(':')[0], 10);
      return hour >= 18 && hour <= 23;
    })
    .map((item) => {
      const [h, m] = item.startTime.split(':').map(Number);
      const raw = item.date ?? item.createdAt;
      const dateStr = localDateStrFromRaw(raw);
      if (!dateStr) return null;
      const fullDt = new Date(`${dateStr}T${item.startTime}`);
      return isNaN(fullDt.getTime()) ? null : { startMinutes: h * 60 + m, fullDt };
    })
    .filter(Boolean)
    .sort((a, b) => b.fullDt - a.fullDt)
    .slice(0, maxNights);

  if (candidates.length < minSamples) return null;

  const mean = candidates.reduce((s, c) => s + c.startMinutes, 0) / candidates.length;
  const variance = candidates.reduce((s, c) => s + (c.startMinutes - mean) ** 2, 0) / candidates.length;
  return { stdDevMinutes: Math.round(Math.sqrt(variance)), sampleCount: candidates.length };
}

// ── Sleep type classification ────────────────────────────────────────────────

/**
 * Classifies a sleep session as 'night' or 'nap' using start time and
 * duration. Designed for auto-labelling from the timer and the manual form.
 *
 * Rules (checked in order):
 *   1. Start ≥ 20:00 or start < 05:00           → night  (clearly bedtime / overnight)
 *   2. Duration ≥ 300 min (5 h)                  → night  (too long to be a nap)
 *   3. Start ≥ 17:00 AND duration ≥ 150 min (2.5h) → night (early bedtime)
 *   4. Otherwise                                 → nap
 *
 * @param {string} startTimeHHMM    "HH:MM" — the session start time
 * @param {number} [durationMinutes] optional; if omitted only rule 1 applies
 * @returns {'night'|'nap'}
 */
export function classifySleepType(startTimeHHMM, durationMinutes = 0) {
  if (!startTimeHHMM) return 'nap';
  const hour = parseInt(startTimeHHMM.split(':')[0], 10);

  if (hour >= 20 || hour < 5)                          return 'night'; // rule 1
  if (durationMinutes >= 300)                          return 'night'; // rule 2
  if (hour >= 17 && durationMinutes >= 150)            return 'night'; // rule 3
  return 'nap';
}

// ── Formatting ──────────────────────────────────────────────────────────────

/** Formats a minute count as "1h 25m", "45m", or "2h". */
export function formatMinutes(min) {
  if (min == null || min < 0) return '—';
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  const h   = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

/**
 * Formats minutes-since-midnight as a 12-hour clock string, e.g. "7:45 PM".
 *
 * @param {number} minutes - minutes since midnight (0–1439)
 * @returns {string}
 */
export function formatBedtime(minutes) {
  if (minutes == null || minutes < 0) return '—';
  const h24 = Math.floor(minutes / 60) % 24;
  const m   = minutes % 60;
  const h12 = h24 % 12 || 12;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}
